<template>
  <detail-page-shell
    :back-to="contractReturnPath || ''"
    :use-history-back="contractDetailBackUsesHistory"
    :back-text="contractBackLabel"
    compact-header
  >
    <template slot="actions">
      <el-button
        v-if="c"
        type="primary"
        size="small"
        :icon="c.linkedProjectId ? 'el-icon-folder-opened' : 'el-icon-plus'"
        @click="onProjectHeaderAction"
      >
        {{ c.linkedProjectId ? $t('contractDetailPage.openProject') : $t('contractDetailPage.toProject') }}
      </el-button>
    </template>
    <el-card v-loading="loading" shadow="never" class="main-card">
      <template v-if="c">
        <el-tabs v-model="tab" class="detail-tabs">
          <el-tab-pane :label="$t('contractDetailPage.tabBase')" name="b">
            <div class="contract-detail__tab-pane-body">
                <!-- ===== DocuSign-style envelope header ===== -->
                <div class="ds-envelope" v-if="c">
                  <div class="ds-envelope__main">
                    <div class="ds-envelope__row">
                      <div>
                        <div class="ds-envelope__eyebrow">ENVELOPE</div>
                        <div class="ds-envelope__no">{{ c.contractNo }}</div>
                        <div class="ds-envelope__title">{{ c.contractTitle || '—' }}</div>
                        <p v-if="showEsignPanel && c.esignFlowCode == null" class="ds-envelope__legacy-tip">
                          {{ $t('contractDetailPage.esignLegacy') }}
                        </p>
                      </div>
                      <div class="ds-envelope__badge" :class="'ds-envelope__badge--' + envelopeStatus.tone">
                        {{ envelopeStatus.label }}
                      </div>
                    </div>

                    <div class="ds-envelope__cta">
                      <el-button
                        v-if="canSendFinal"
                        type="success"
                        size="medium"
                        :loading="esignFinalLoading"
                        @click="doSendFinal"
                      >{{ $t('contractDetailPage.sendFinal') }}</el-button>
                      <el-button
                        v-if="hasContractDocument"
                        type="primary"
                        size="medium"
                        icon="el-icon-download"
                        @click="downloadContractDocument"
                      >{{ downloadContractLabel }}</el-button>
                      <el-button
                        v-if="canRegenerateDocument"
                        size="medium"
                        plain
                        icon="el-icon-refresh"
                        :loading="regenerateLoading"
                        @click="doRegenerateDocument"
                      >{{ $t('contractDetailPage.remerge') }}</el-button>
                    </div>
                  </div>
                </div>

                <!-- ===== Contract document (expand to preview) ===== -->
                <el-card v-if="hasContractDocument" shadow="never" class="section signed-preview-card">
                  <div slot="header" class="section-hdr section-hdr--split">
                    <span>{{ contractPreviewTitle }}</span>
                    <div class="section-hdr__actions">
                      <el-button
                        type="text"
                        size="small"
                        :icon="contractPreviewOpen ? 'el-icon-arrow-up' : 'el-icon-view'"
                        @click="toggleContractPreview"
                      >{{ contractPreviewOpen ? $t('contractDetailPage.hideContractPreview') : $t('contractDetailPage.showContractPreview') }}</el-button>
                      <el-button type="text" size="small" @click="downloadContractDocument">
                        {{ $t('contractDetailPage.downloadContractDoc') }}
                      </el-button>
                    </div>
                  </div>
                  <div v-show="contractPreviewOpen" class="signed-pdf-wrap" ref="signedPdfScroller">
                    <div v-show="signedPdfLoading" class="signed-pdf-loading">{{ $t('contractDetailPage.signedPdfLoading') }}</div>
                    <canvas
                      v-for="(p, i) in signedPdfPages"
                      :key="'signed-' + i"
                      :ref="'signedPdfCanvas' + i"
                      class="signed-pdf-canvas"
                    ></canvas>
                    <p v-if="signedPdfError" class="signed-pdf-error text-muted">{{ signedPdfError }}</p>
                  </div>
                  <p v-if="!contractPreviewOpen" class="contract-preview-collapsed-hint text-muted">
                    {{ $t('contractDetailPage.contractPreviewCollapsedHint') }}
                  </p>
                </el-card>

                <!-- ===== Recipients ===== -->
                <div class="ds-recipients" v-if="c">
                  <div class="ds-recipient" :class="'ds-recipient--' + customerRecipient.state">
                    <div class="ds-recipient__avatar">{{ initials(customerRecipient.name) }}</div>
                    <div class="ds-recipient__body">
                      <div class="ds-recipient__role">Customer</div>
                      <div class="ds-recipient__name">{{ customerRecipient.name || '—' }}</div>
                      <div class="ds-recipient__email">{{ customerRecipient.email || c.esignRecipientEmail || '—' }}</div>
                      <div class="ds-recipient__state">
                        <el-tag size="mini" :type="customerRecipient.tag">{{ customerRecipient.stateLabel }}</el-tag>
                        <span v-if="c.customerSignedAt" class="ds-recipient__when">
                          {{ formatDisplayDateTime(c.customerSignedAt) }}
                        </span>
                      </div>
                      <div
                        v-if="showEsignPanel && canEsignActions && inviteEmailRowVisible"
                        class="ds-recipient__invite"
                      >
                        <el-input
                          v-model="esignRecipientEmail"
                          size="small"
                          clearable
                          :placeholder="$t('contractDetailPage.esignRecipientPh')"
                          class="ds-recipient__invite-input"
                        />
                        <el-button
                          v-if="canSendInvite"
                          type="primary"
                          size="small"
                          :loading="esignSendLoading"
                          @click="doSendInvite"
                        >{{ $t('contractDetailPage.sendInvite') }}</el-button>
                      </div>
                    </div>
                    <div class="ds-recipient__thumb" v-if="signaturePreviewUrl">
                      <img :src="signaturePreviewUrl" alt="customer signature" />
                    </div>
                  </div>

                  <div class="ds-recipient" :class="'ds-recipient--' + companyRecipient.state">
                    <div class="ds-recipient__avatar ds-recipient__avatar--co">{{ initials(companyRecipient.name || 'EHD') }}</div>
                    <div class="ds-recipient__body">
                      <div class="ds-recipient__role">Company</div>
                      <div class="ds-recipient__name">{{ companyRecipient.name || 'EHD Builders' }}</div>
                      <div class="ds-recipient__email">Company signer</div>
                      <div class="ds-recipient__state">
                        <el-tag size="mini" :type="companyRecipient.tag">{{ companyRecipient.stateLabel }}</el-tag>
                        <span v-if="c.companySignedAt" class="ds-recipient__when">
                          {{ formatDisplayDateTime(c.companySignedAt) }}
                        </span>
                      </div>
                      <div v-if="canCompanySign" class="ds-recipient__company-act">
                        <el-button type="primary" size="small" @click="openCompanySign">{{
                          $t('contractDetailPage.companySign')
                        }}</el-button>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- ===== Activity timeline ===== -->
                <el-card v-if="c" shadow="never" class="section ds-timeline-card">
                  <div slot="header" class="section-hdr section-hdr--split">
                    <span>Activity timeline</span>
                    <span>
                      <el-checkbox v-model="showViewAudits" size="small" style="margin-right:12px">
                        {{ $t('contractDetailPage.showViewAudits') }}
                      </el-checkbox>
                      <el-button type="text" size="small" @click="openEsignTrailDialog">Full audit trail</el-button>
                    </span>
                  </div>
                  <div v-if="auditLoading" class="ds-timeline-loading">Loading…</div>
                  <div v-else-if="!timelineDisplayed.length" class="ds-timeline-empty">No activity recorded yet.</div>
                  <el-timeline v-else>
                    <el-timeline-item
                      v-for="row in timelineDisplayed"
                      :key="row.id || (row.eventType + row.createdAt)"
                      :type="timelineColor(row.eventType)"
                      :icon="timelineIcon(row.eventType)"
                      :timestamp="formatDisplayDateTime(row.createdAt) || '—'"
                      placement="top"
                    >
                      <div class="ds-timeline-row">
                        <strong>{{ auditEventLabel(row.eventType) }}</strong>
                        <span v-if="row.signerName"> · {{ row.signerName }}</span>
                        <span v-if="row.signerEmail" class="text-muted"> · {{ row.signerEmail }}</span>
                        <span v-if="row.ipAddress" class="text-muted"> · IP {{ row.ipAddress }}</span>
                      </div>
                      <div v-if="row.remarkText" class="ds-timeline-note">{{ row.remarkText }}</div>
                    </el-timeline-item>
                  </el-timeline>
                </el-card>

                <div class="contract-detail__summary">
              <el-descriptions border :column="2" size="small" class="desc-block desc-block--flush">
                <el-descriptions-item :label="$t('contractDetailPage.quoteNo')">{{ c.linkedQuoteNo || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.template')">{{ c.contractTemplateName || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.linkedProject')">
                  <template v-if="c.linkedProjectId">
                    <router-link :to="contractProjectLinkTo" class="link">{{ c.linkedProjectCode || $t('contractDetailPage.linkedProjectFallback', { id: c.linkedProjectId }) }}</router-link>
                  </template>
                  <span v-else class="text-muted">—</span>
                </el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.quoteSnapshot')">{{ formatUsd(c.quoteTotalAmount) }} {{ c.currencyCode || 'USD' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.startDate')">{{ c.startDate || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.endDate')">{{ c.endDate || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.termMonths')">{{ c.contractTermMonths != null ? c.contractTermMonths : '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.signer')">{{ c.customerSignedName || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.signedAt')">{{ formatDisplayDateTime(c.customerSignedAt || c.signedAt) || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.signEmail')">{{ c.customerSignedEmail || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.signIp')">{{ c.customerSignedIp || '—' }}</el-descriptions-item>
                <el-descriptions-item :label="$t('contractDetailPage.createdAt')">{{ formatDisplayDateTime(c.createdAt) || '—' }}</el-descriptions-item>
              </el-descriptions>
                </div>

                <div v-if="c.linkedProjectId && c.id" class="contract-detail__docs-grid">
              <el-card
                v-loading="linkedAttachmentsLoading"
                shadow="never"
                class="section section--doc"
              >
                <div slot="header" class="section-hdr">
                  <span>{{ $t('contractDetailPage.contractExtraSection') }}</span>
                </div>

                <p v-if="!contractExtraAttachments.length" class="text-muted">{{ $t('contractDetailPage.extraAttachEmpty') }}</p>
                <ul v-else class="extra-att-list">
                  <li v-for="row in contractExtraAttachments" :key="'cx' + row.id">
                    <span class="extra-att-list__name">{{ row.fileName || '—' }}</span>
                    <el-button type="text" size="small" @click="previewProjectAttachment(row)">{{
                      $t('contractDetailPage.previewExtraAttach')
                    }}</el-button>
                    <el-button type="text" size="small" @click="downloadProjectAttachment(row)">{{
                      $t('contractDetailPage.downloadExtraAttach')
                    }}</el-button>
                  </li>
                </ul>
                <div v-if="canManageQuoteContractExtras && c.id" class="btn-row upload-row">
                  <input
                    ref="contractExtraFileInput"
                    type="file"
                    class="hidden-file"
                    accept=".pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.zip"
                    @change="onContractExtraFile"
                  />
                  <el-button size="small" icon="el-icon-upload2" @click="$refs.contractExtraFileInput.click()">{{
                    $t('contractDetailPage.uploadFile')
                  }}</el-button>
                </div>
              </el-card>
                </div>

                <el-card shadow="never" class="section contract-detail__co-section contract-detail__co-in-base">
                  <div slot="header" class="section-hdr section-hdr--split">
                    <span>{{ $t('contractDetailPage.tabChangeOrders') }}</span>
                    <el-button type="text" size="small" class="section-hdr__action" @click="$router.push('/change-order/list')">{{ $t('contractDetailPage.changeOrdersAllNav') }}</el-button>
                  </div>
                  <div class="co-toolbar co-toolbar--inline">
                    <el-button type="primary" size="small" icon="el-icon-plus" @click="openCoNew">{{ $t('pipelineList.newChangeOrder') }}</el-button>
                  </div>
                  <el-table
                    :data="changeOrders"
                    border
                    stripe
                    size="small"
                    v-loading="coLoading"
                    :empty-text="$t('common.empty')"
                    class="co-table"
                  >
                    <el-table-column prop="changeNo" :label="$t('grid.number')" width="140" show-overflow-tooltip />
                    <el-table-column :label="$t('grid.amountDelta')" width="120" align="right">
                      <template slot-scope="s">{{ formatUsd(s.row.amountDelta) }}</template>
                    </el-table-column>
                    <el-table-column prop="descriptionText" :label="$t('grid.description')" min-width="160" show-overflow-tooltip />
                    <el-table-column :label="$t('grid.approval')" width="100">
                      <template slot-scope="s">{{ labelChangeOrderApproval(s.row.approvalStatusCode) }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('grid.createdAt')" width="190">
                      <template slot-scope="s">{{ formatDisplayDateTime(s.row.createdAt) || '—' }}</template>
                    </el-table-column>
                    <el-table-column :label="$t('common.actions')" width="200" fixed="right">
                      <template slot-scope="s">
                        <el-button v-if="canOfficeAct && changeOrderCanApproveReject(s.row.approvalStatusCode)" type="text" size="small" @click="onApproveChangeOrder(s.row)">{{ $t('publicQuote.approve') }}</el-button>
                        <el-button v-if="canOfficeAct && changeOrderCanApproveReject(s.row.approvalStatusCode)" type="text" size="small" class="danger-link" @click="onRejectChangeOrder(s.row)">{{ $t('publicQuote.reject') }}</el-button>
                      </template>
                    </el-table-column>
                  </el-table>
                  <div class="list-pager">
                    <el-pagination
                      background
                      layout="total, sizes, prev, pager, next"
                      :page-sizes="[10, 20, 50]"
                      :total="coTotal"
                      :page-size="coPageSize"
                      :current-page="coPageNo"
                      @size-change="onCoSizeChange"
                      @current-change="p => { coPageNo = p; loadChangeOrders() }"
                    />
                  </div>
                </el-card>
            </div>
          </el-tab-pane>

          <el-tab-pane :label="$t('contractDetailPage.tabPlans')" name="p">
            <div class="contract-detail__tab-pane-body">
              <el-card shadow="never" class="section contract-detail__plan-card">
                  <div slot="header" class="section-hdr">
                    <span>{{ $t('contractDetailPage.tabPlans') }}</span>
                  </div>
                  <p class="plan-hint">{{ planHintText }}</p>
                    <div class="plan-stats" role="group" :aria-label="$t('contractDetailPage.tabPlans')">
                      <div class="plan-stat-card" :title="$t('contractDetailPage.planStatContractAmtTip')">
                        <span class="plan-stat-card__label">{{ $t('contractDetailPage.planStatContractAmt') }}</span>
                        <strong class="plan-stat-card__value">{{ formatUsd(planStatsContractAmount) }}</strong>
                      </div>
                      <div class="plan-stat-card">
                        <span class="plan-stat-card__label">{{ $t('contractDetailPage.planStatPlannedTotal') }}</span>
                        <strong class="plan-stat-card__value">{{ formatUsd(planStatsPlannedTotal) }}</strong>
                      </div>
                      <div class="plan-stat-card" :title="$t('contractDetailPage.planStatUnplannedTip')">
                        <span class="plan-stat-card__label">{{ $t('contractDetailPage.planStatUnplanned') }}</span>
                        <strong class="plan-stat-card__value">{{ formatUsd(planStatsUnplanned) }}</strong>
                      </div>
                    </div>
                    <div v-if="canEditPaymentPlans" class="plan-toolbar">
                      <el-button type="primary" size="small" icon="el-icon-plus" @click="openPlanNew">{{ $t('contractDetailPage.addPlanNode') }}</el-button>
                    </div>
                    <el-table :data="plans" border stripe size="small" class="plan-table" :empty-text="$t('contractDetailPage.emptyPlans')">
                      <el-table-column prop="nodeSeq" label="#" width="50" align="center" />
                      <el-table-column prop="nodeName" :label="$t('contractDetailPage.nodeCol')" min-width="120" show-overflow-tooltip />
                      <el-table-column :label="$t('contractDetailPage.plannedAmt')" min-width="120" align="right">
                        <template slot-scope="s">{{ formatUsd(s.row.plannedAmount) }}</template>
                      </el-table-column>
                      <el-table-column prop="plannedDate" :label="$t('contractDetailPage.plannedDate')" min-width="140" />
                      <el-table-column v-if="planSource === 'project'" :label="$t('common.status')" min-width="100">
                        <template slot-scope="s">{{ labelPaymentPlanStatus(s.row.paymentStatusCode) }}</template>
                      </el-table-column>
                      <el-table-column v-if="canEditPaymentPlans" :label="$t('common.actions')" min-width="140" align="center" fixed="right">
                        <template slot-scope="s">
                          <template v-if="isPaymentPlanEditable(s.row)">
                            <el-button type="text" size="small" @click="openPlanEdit(s.row)">{{ $t('common.edit') }}</el-button>
                            <el-button type="text" size="small" class="danger-link" @click="removePlan(s.row)">{{ $t('common.delete') }}</el-button>
                          </template>
                          <span v-else class="text-muted">—</span>
                        </template>
                      </el-table-column>
                    </el-table>
              </el-card>
            </div>
          </el-tab-pane>
        </el-tabs>
      </template>

      <form-dialog
        :title="planEditRow ? $t('contractDetailPage.editPlanTitle') : $t('contractDetailPage.addPlanNode')"
        :visible.sync="planDlg"
        width="520px"
        @closed="onPlanDlgClosed"
      >
        <el-form v-if="planForm" label-width="100px" size="small">
          <el-form-item :label="$t('contractDetailPage.nodeName')" required>
            <el-input v-model="planForm.nodeName" clearable />
          </el-form-item>
          <el-form-item :label="$t('contractDetailPage.planAmt')" required>
            <el-input v-model="planForm.plannedAmount" :placeholder="$t('contractDetailPage.planAmtPh')" clearable />
          </el-form-item>
          <el-form-item :label="$t('contractDetailPage.plannedDate')" required>
            <el-date-picker v-model="planForm.plannedDate" type="date" value-format="yyyy-MM-dd" :placeholder="$t('contractDetailPage.planDatePh')" style="width:100%;" />
          </el-form-item>
          <el-form-item :label="$t('common.notes')">
            <el-input v-model="planForm.remarkText" type="textarea" :rows="2" />
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="planDlg = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="planSaving" @click="savePlanEdit">{{ $t('common.save') }}</el-button>
        </span>
      </form-dialog>

      <el-dialog
        :title="$t('contractDetailPage.esignTrail')"
        :visible.sync="esignTrailDlg"
        width="90%"
        top="4vh"
        append-to-body
        custom-class="contract-esign-trail-dlg"
        @opened="loadAudits"
      >
        <div v-if="c" class="esign-trail-dlg-inner">
          <div v-if="auditViewRowCount > 0" class="esign-trail-dlg-toolbar">
            <el-checkbox v-model="showViewAudits" size="small">{{ $t('contractDetailPage.showViewAudits') }}</el-checkbox>
          </div>
          <div class="esign-grid">
            <div v-if="signaturePreviewUrl" class="esign-preview">
              <img :src="signaturePreviewUrl" alt="customer signature" class="esign-image" />
            </div>
            <div class="esign-meta">
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignSigner') }}</span><strong>{{ c.customerSignedName || '—' }}</strong></div>
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignEmail') }}</span><strong>{{ c.customerSignedEmail || '—' }}</strong></div>
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignTime') }}</span><strong>{{ formatDisplayDateTime(c.customerSignedAt || c.signedAt) || '—' }}</strong></div>
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignIp') }}</span><strong>{{ c.customerSignedIp || '—' }}</strong></div>
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignUa') }}</span><strong class="ua-text">{{ c.customerSignedUserAgent || '—' }}</strong></div>
              <div class="esign-row"><span>{{ $t('contractDetailPage.esignNote') }}</span><strong>{{ c.customerSignedNote || '—' }}</strong></div>
              <div class="btn-row" v-if="c.customerSignatureImageUrl">
                <el-button size="small" icon="el-icon-download" @click="downloadCustomerSignature">{{ $t('contractDetailPage.downloadSigImg') }}</el-button>
              </div>
            </div>
          </div>
          <p v-if="auditViewHiddenCount && !showViewAudits" class="audit-filter-hint muted">
            {{ $t('contractDetailPage.auditViewHiddenHint', { count: auditViewHiddenCount }) }}
          </p>
          <el-table
            :data="auditListDisplayed"
            :empty-text="auditTableEmptyText"
            border
            size="small"
            class="esign-audit-table esign-audit-table--dlg"
            v-loading="auditLoading"
          >
            <el-table-column :label="$t('contractDetailPage.auditEvent')" width="110">
              <template slot-scope="scope">
                <el-tag size="mini" :type="auditEventTagType(scope.row.eventType)">
                  {{ auditEventLabel(scope.row.eventType) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column :label="$t('contractDetailPage.auditTime')" width="210">
              <template slot-scope="scope">{{ formatDisplayDateTime(scope.row.createdAt) || '—' }}</template>
            </el-table-column>
            <el-table-column prop="signerName" :label="$t('contractDetailPage.auditSigner')" width="130" show-overflow-tooltip />
            <el-table-column prop="signerEmail" :label="$t('contractDetailPage.auditEmail')" min-width="160" show-overflow-tooltip />
            <el-table-column prop="ipAddress" :label="$t('contractDetailPage.auditIp')" width="130" show-overflow-tooltip />
            <el-table-column prop="remarkText" :label="$t('contractDetailPage.auditRemark')" min-width="160" show-overflow-tooltip />
            <el-table-column :label="$t('contractDetailPage.auditUa')" min-width="220" show-overflow-tooltip>
              <template slot-scope="scope">{{ scope.row.userAgent || '—' }}</template>
            </el-table-column>
          </el-table>
        </div>
      </el-dialog>

      <form-dialog :title="$t('contractDetailPage.toProject')" :visible.sync="projectDlg" width="480px">
        <p class="section-tip project-dlg-tip">{{ $t('contractDetailPage.toProjectDlgHint') }}</p>
        <el-form label-width="100px" size="small" class="project-dlg-form">
          <el-form-item :label="$t('contractDetailPage.pmLabel')">
            <el-select
              v-model="pmUserId"
              filterable
              clearable
              :placeholder="$t('contractDetailPage.pmPh')"
              :loading="pmListLoading"
              class="project-dlg-pm"
            >
              <el-option
                v-for="o in pmOptions"
                :key="o.value"
                :label="o.label"
                :value="o.value"
              />
            </el-select>
          </el-form-item>
          <el-form-item :label="$t('contractDetailPage.pjName')">
            <el-input v-model="pjName" :placeholder="$t('contractDetailPage.pjNamePh')" clearable />
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="projectDlg = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="projectCreating" @click="createProject">{{ $t('contractDetailPage.createProject') }}</el-button>
        </span>
      </form-dialog>

      <form-dialog :title="$t('changeOrderPage.newDialog')" :visible.sync="coDlg" width="520px" @closed="resetCoForm">
        <el-form label-width="100px" size="small">
          <el-form-item :label="$t('changeOrderPage.linkContract')">
            <el-input :value="c ? (c.contractNo || ('#' + c.id)) : ''" disabled />
          </el-form-item>
          <el-form-item :label="$t('grid.amountDelta')">
            <el-input v-model="coForm.amountDelta" :placeholder="$t('changeOrderPage.amountPh')" clearable />
          </el-form-item>
          <el-form-item :label="$t('changeOrderPage.descLabel')">
            <el-input v-model="coForm.descriptionText" type="textarea" :rows="3" :placeholder="$t('changeOrderPage.descPh')" />
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="coDlg = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="coSaving" @click="saveCoNew">{{ $t('common.save') }}</el-button>
        </span>
      </form-dialog>

      <form-dialog
        :title="$t('contractDetailPage.companySignTitle')"
        :visible.sync="companySignDlg"
        width="720px"
        @opened="onCompanySignOpened"
        @closed="resetCompanySign"
      >
        <p class="ds-company-sign__lead">
          Review the contract content, then adopt your signature on behalf of EHD Builders.
        </p>
        <div class="ds-company-sign__row">
          <el-button v-if="hasContractDocument" size="small" icon="el-icon-view" @click="previewMerged">
            Preview contract
          </el-button>
        </div>
        <el-form label-width="120px" size="small">
          <el-form-item :label="$t('contractDetailPage.companySignerName')">
            <el-input v-model="companySignForm.signerName" />
          </el-form-item>
          <el-form-item :label="$t('publicContract.signature')">
            <div class="ds-method-tabs ds-method-tabs--admin">
              <button
                type="button"
                class="ds-method-tab"
                :class="{ 'ds-method-tab--active': companyMethod === 'draw' }"
                @click="setCompanyMethod('draw')"
              >Draw</button>
              <button
                type="button"
                class="ds-method-tab"
                :class="{ 'ds-method-tab--active': companyMethod === 'type' }"
                @click="setCompanyMethod('type')"
              >Type</button>
            </div>

            <div v-show="companyMethod === 'draw'" class="signature-pad contract-company-pad">
              <canvas
                ref="companySignCanvas"
                class="signature-pad__canvas"
                width="560"
                height="200"
                @mousedown="startCompanySign"
                @mousemove="moveCompanySign"
                @mouseup="endCompanySign"
                @mouseleave="endCompanySign"
                @touchstart.prevent="startCompanySign"
                @touchmove.prevent="moveCompanySign"
                @touchend.prevent="endCompanySign"
              ></canvas>
            </div>
            <div v-show="companyMethod === 'draw'" class="ds-company-pane-actions">
              <el-button size="mini" @click="clearCompanySign">{{ $t('publicContract.clear') }}</el-button>
            </div>

            <div v-show="companyMethod === 'type'" class="ds-company-type">
              <el-input
                v-model="companyTypeName"
                size="small"
                placeholder="Type the name to sign"
                style="margin-bottom: 10px"
              />
              <div class="ds-type-grid">
                <div
                  v-for="font in TYPE_FONTS_ADMIN"
                  :key="font.family"
                  class="ds-type-option"
                  :class="{ 'ds-type-option--active': companyTypeFontIndex === font.idx }"
                  :style="{ fontFamily: font.css }"
                  @click="companyTypeFontIndex = font.idx"
                >
                  <span class="ds-type-sample">{{ companyTypeName || companySignForm.signerName || 'Signer' }}</span>
                  <span class="ds-type-style">{{ font.label }}</span>
                </div>
              </div>
            </div>
          </el-form-item>
        </el-form>
        <span slot="footer">
          <el-button @click="companySignDlg = false">{{ $t('common.cancel') }}</el-button>
          <el-button type="primary" :loading="companySigning" @click="submitCompanySign">
            {{ $t('common.confirm') }}
          </el-button>
        </span>
      </form-dialog>
    </el-card>
  </detail-page-shell>
</template>

<script>
import {
  companySignContract,
  deleteContractPaymentPlan,
  downloadContractDocumentUrl,
  downloadCustomerSignatureUrl,
  getContractDetail,
  listContractPaymentPlans,
  listContractSignAudits,
  previewContractDocumentUrl,
  regenerateContractDocument,
  saveContractPaymentPlan,
  sendContractToCustomer,
  sendFinalContractToCustomer,
  updateContractPaymentPlan
} from '@/api/contract'
import { getChangeOrderPage, createChangeOrder, approveChangeOrder, rejectChangeOrder } from '@/api/changeOrder'
import { createProjectFromContract } from '@/api/project'
import { getProjectWorkspace, uploadProjectAttachment, downloadAttachmentUrl, previewAttachmentUrl } from '@/api/projectFinance'
import { getProjectManagerPickerList } from '@/api/user'
import { deletePaymentPlan, listPaymentPlans, savePaymentPlan, updatePaymentPlan } from '@/api/paymentPlan'
import {
  labelPaymentPlanStatus,
  labelChangeOrderApproval,
  changeOrderCanApproveReject
} from '@/utils/crmLabels'
import { formatUsd } from '@/utils/formatUsd'
import { formatDisplayDateTime } from '@/utils/formatDisplayDateTime'
import { downloadBlobAuthenticated, previewBlobAuthenticated } from '@/utils/downloadBlobAuth'
import axios from 'axios'
import store from '@/store'
import DetailPageShell from '@/components/DetailPageShell.vue'
import FormDialog from '@/components/FormDialog.vue'
import { decodeReturnQuery, projectDetailLocation } from '@/utils/contractDetailNav'

const ADMIN_TYPE_FONTS = [
  { idx: 0, family: 'Caveat', label: 'Caveat', css: "'Caveat', cursive" },
  { idx: 1, family: 'Dancing Script', label: 'Dancing', css: "'Dancing Script', cursive" },
  { idx: 2, family: 'Great Vibes', label: 'Great Vibes', css: "'Great Vibes', cursive" },
  { idx: 3, family: 'Allura', label: 'Allura', css: "'Allura', cursive" }
]
const ADMIN_TYPE_FONTS_LINK_ID = 'ds-admin-type-fonts-link'
const ADMIN_TYPE_FONTS_HREF =
  'https://fonts.googleapis.com/css2?family=Caveat&family=Dancing+Script&family=Allura&family=Great+Vibes&display=swap'

export default {
  name: 'ContractDetail',
  components: { DetailPageShell, FormDialog },
  data() {
    return {
      TYPE_FONTS_ADMIN: ADMIN_TYPE_FONTS,
      loading: false,
      tab: 'b',
      c: null,
      pmUserId: null,
      pmOptions: [],
      pmListLoading: false,
      pjName: '',
      plans: [],
      planSource: 'contract',
      planDlg: false,
      planEditRow: null,
      planForm: null,
      planSaving: false,
      signaturePreviewUrl: '',
      auditList: [],
      auditLoading: false,
      showViewAudits: false,
      esignTrailDlg: false,
      projectDlg: false,
      projectCreating: false,
      changeOrders: [],
      coLoading: false,
      coTotal: 0,
      coPageNo: 1,
      coPageSize: 10,
      coDlg: false,
      coSaving: false,
      coForm: {
        amountDelta: '',
        descriptionText: ''
      },
      approvedChangeOrderDeltaSum: 0,
      linkedAttachmentsForProject: [],
      linkedAttachmentsLoading: false,
      esignRecipientEmail: '',
      esignSendLoading: false,
      esignFinalLoading: false,
      signedPdfDoc: null,
      signedPdfPages: [],
      signedPdfLoading: false,
      signedPdfError: '',
      signedPdfLoadedKey: '',
      contractPreviewOpen: false,
      regenerateLoading: false,
      companySignDlg: false,
      companySigning: false,
      companySignForm: {
        signerName: ''
      },
      companySignDrawing: false,
      companySignHasStroke: false,
      companyMethod: 'draw',
      companyTypeName: '',
      companyTypeFontIndex: 0
    }
  },
  computed: {
    envelopeStatus() {
      if (!this.c) return { tone: 'neutral', label: 'Loading…' }
      const status = Number(this.c.contractStatusCode)
      if (status === 5) return { tone: 'cancelled', label: 'Cancelled' }
      // 旧归档数据（status=4）统一展示为「Completed」，归档动作已与下载合并。
      if (status === 4) return { tone: 'completed', label: 'Completed' }
      const f = this.c.esignFlowCode == null ? null : Number(this.c.esignFlowCode)
      if (f == null) return { tone: 'draft', label: 'Draft' }
      if (f <= 10) return { tone: 'draft', label: 'Draft' }
      if (f <= 20) return { tone: 'pending', label: 'Waiting for customer' }
      if (f <= 30) return { tone: 'progress', label: 'Customer signed' }
      if (f <= 40) return { tone: 'progress', label: 'Both signed · ready to deliver' }
      return { tone: 'completed', label: 'Completed' }
    },
    customerRecipient() {
      const c = this.c || {}
      const signed = !!c.customerSignedAt
      const sent = !!c.sentToCustomerAt
      let state = 'pending'
      let stateLabel = 'Pending'
      let tag = 'info'
      if (signed) {
        state = 'signed'
        stateLabel = 'Signed'
        tag = 'success'
      } else if (sent) {
        state = 'sent'
        stateLabel = 'Sent · waiting'
        tag = 'warning'
      } else {
        state = 'pending'
        stateLabel = 'Not sent'
        tag = 'info'
      }
      return {
        name: c.customerSignedName || c.linkedQuoteCustomerDisplayName || '',
        email: c.customerSignedEmail || c.esignRecipientEmail || '',
        state,
        stateLabel,
        tag
      }
    },
    companyRecipient() {
      const c = this.c || {}
      const signed = !!c.companySignedAt
      let state = signed ? 'signed' : 'pending'
      let stateLabel = signed ? 'Signed' : 'Awaiting company signature'
      let tag = signed ? 'success' : 'info'
      return {
        name: c.companySignerName || '',
        state,
        stateLabel,
        tag
      }
    },
    timelineDisplayed() {
      const list = Array.isArray(this.auditList) ? this.auditList : []
      const filtered = this.showViewAudits ? list : list.filter(r => r && r.eventType !== 'VIEWED' && r.eventType !== 'SIGN_VIEW_START')
      return filtered.slice().reverse()
    },
    id() {
      return this.$route.params.id
    },
    contractReturnPath() {
      return decodeReturnQuery(this.$route.query.return)
    },
    /** 无 return 参数时用历史返回，避免一律回合同列表 */
    contractDetailBackUsesHistory() {
      return !this.contractReturnPath
    },
    contractBackLabel() {
      return this.$t('common.back')
    },
    contractProjectLinkTo() {
      if (!this.c || !this.c.linkedProjectId) return { path: '/project/list' }
      return projectDetailLocation(this.c.linkedProjectId, this.$route.fullPath)
    },
    canOfficeAct() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      return roles.includes('OFFICE') || roles.includes('ADMIN')
    },
    canMutatePaymentPlans() {
      const u = store.state.userInfo || {}
      const roles = u.roleCodes || []
      if (roles.includes('OFFICE') || roles.includes('ADMIN') || roles.includes('SALES')) return true
      return roles.includes('PM') && !!u.pmCanMutateProjectFinance
    },
    canEditPaymentPlans() {
      if (!this.canMutatePaymentPlans) return false
      if (this.c && this.c.linkedProjectId) return this.planSource === 'project'
      return this.planSource === 'contract'
    },
    planHintText() {
      if (this.c && this.c.linkedProjectId) return this.$t('contractDetailPage.planHint')
      return this.$t('contractDetailPage.planHintContract')
    },
    auditViewRowCount() {
      return this.auditList.filter((r) => r && r.eventType === 'VIEWED').length
    },
    auditViewHiddenCount() {
      return this.showViewAudits ? 0 : this.auditViewRowCount
    },
    auditListDisplayed() {
      if (this.showViewAudits) return this.auditList
      return this.auditList.filter((r) => !r || r.eventType !== 'VIEWED')
    },
    auditTableEmptyText() {
      if (this.auditLoading) return ''
      if (this.auditList.length && !this.auditListDisplayed.length) {
        return this.$t('contractDetailPage.auditTableEmptyFiltered')
      }
      return ''
    },
    /** 报价快照 + 已批准变更单 Δ金额 */
    planStatsContractAmount() {
      const base = this.c && this.c.quoteTotalAmount != null && this.c.quoteTotalAmount !== ''
        ? Number(this.c.quoteTotalAmount)
        : 0
      const co = Number(this.approvedChangeOrderDeltaSum) || 0
      const sum = (Number.isNaN(base) ? 0 : base) + (Number.isNaN(co) ? 0 : co)
      return sum
    },
    planStatsPlannedTotal() {
      return (this.plans || []).reduce((acc, p) => acc + (Number(p.plannedAmount) || 0), 0)
    },
    /** 合同金额 − 计划合计，不低于 0 */
    planStatsUnplanned() {
      const diff = this.planStatsContractAmount - this.planStatsPlannedTotal
      return diff > 0 ? diff : 0
    },
    quoteExtraAttachments() {
      if (!this.c || !this.c.quoteId) return []
      const qid = Number(this.c.quoteId)
      return (this.linkedAttachmentsForProject || []).filter(
        row => row && row.kind === 'quoteAttach' && row.quoteId != null && Number(row.quoteId) === qid
      )
    },
    contractExtraAttachments() {
      if (!this.c || !this.c.id) return []
      const cid = Number(this.c.id)
      return (this.linkedAttachmentsForProject || []).filter(
        row =>
          row && row.kind === 'contractAttach' && row.contractId != null && Number(row.contractId) === cid
      )
    },
    canManageQuoteContractExtras() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      if (roles.includes('OFFICE') || roles.includes('ADMIN') || roles.includes('SALES')) return true
      return !!(roles.includes('PM') && this.c && this.c.linkedProjectId)
    },
    showEsignPanel() {
      if (!this.c) return false
      return !!(this.c.generatedFileUrl || this.c.esignFlowCode != null)
    },
    /** 客户卡片内：收件邮箱与发邀请至终版邮件发送前仍可编辑 */
    inviteEmailRowVisible() {
      if (!this.showEsignPanel || !this.canEsignActions || !this.c) return false
      const f = this.c.esignFlowCode
      if (f == null) return true
      return Number(f) < 50
    },
    canEsignActions() {
      const roles = (store.state.userInfo && store.state.userInfo.roleCodes) || []
      return roles.includes('OFFICE') || roles.includes('ADMIN') || roles.includes('SALES')
    },
    canSendInvite() {
      if (!this.canEsignActions || !this.c || !this.c.generatedFileUrl) return false
      const f = this.c.esignFlowCode
      if (f == null) return true
      return Number(f) < 30
    },
    canCompanySign() {
      return this.canEsignActions && this.c && Number(this.c.esignFlowCode) === 30
    },
    canSendFinal() {
      return this.canEsignActions && this.c && Number(this.c.esignFlowCode) === 40
    },
    /** 是否有任意可预览/下载的合同文件（合并稿、签字版或上传扫描件）。 */
    hasContractDocument() {
      if (!this.c) return false
      return !!(
        this.c.generatedFileUrl ||
        this.c.finalPackageFileUrl ||
        this.c.customerSignedPdfUrl ||
        this.c.signedFileUrl
      )
    },
    /** 未发送给客户前可重新生成合并稿。 */
    canRegenerateDocument() {
      if (!this.canEsignActions || !this.c || !this.c.templateId) return false
      const f = this.c.esignFlowCode
      if (f == null) return true
      return Number(f) < 20
    },
    bothSigned() {
      return !!(this.c && this.c.companySignedAt)
    },
    downloadContractLabel() {
      if (this.bothSigned) return this.$t('contractDetailPage.downloadFullExecuted')
      if (this.c && this.c.customerSignedPdfUrl) {
        return this.$t('contractDetailPage.downloadCustomerSigned')
      }
      return this.$t('contractDetailPage.downloadMerged')
    },
    contractPreviewTitle() {
      if (this.bothSigned) return this.$t('contractDetailPage.signedPreviewBoth')
      if (this.c && this.c.customerSignedPdfUrl) {
        return this.$t('contractDetailPage.signedPreviewCustomer')
      }
      return this.$t('contractDetailPage.contractPreviewDraft')
    },
    /** 用于 watch：合同文件版本变化时重置内嵌预览。 */
    contractDocumentKey() {
      if (!this.c) return ''
      return [
        this.c.generatedFileUrl || '',
        this.c.finalPackageFileUrl || '',
        this.c.customerSignedPdfUrl || '',
        this.c.signedFileUrl || ''
      ].join('|')
    }
  },
  watch: {
    tab(val) {
      if (!this.c) return
      if (val === 'p') this.loadPlans()
    },
    contractDocumentKey(val, oldVal) {
      if (val !== oldVal) {
        this.signedPdfLoadedKey = ''
        if (this.contractPreviewOpen) {
          this.$nextTick(() => this.maybeLoadContractPdf())
        }
      }
    }
  },
  async created() {
    await Promise.all([this.loadContract(), this.loadPmOptions()])
  },
  beforeDestroy() {
    this.cleanupSignaturePreview()
  },
  methods: {
    changeOrderCanApproveReject,
    formatUsd,
    formatDisplayDateTime,
    labelPaymentPlanStatus,
    labelChangeOrderApproval,
    auditEventLabel(type) {
      const map = {
        VIEWED: this.$t('contractDetailPage.auditViewed'),
        DOWNLOADED: this.$t('contractDetailPage.auditDownloaded'),
        SIGNED: this.$t('contractDetailPage.auditSigned'),
        SENT_INVITE_EMAIL: this.$t('contractDetailPage.auditSentInvite'),
        COMPANY_SIGNED: this.$t('contractDetailPage.auditCompanySigned'),
        SENT_FINAL_EMAIL: this.$t('contractDetailPage.auditFinalEmail'),
        ARCHIVED: this.$t('contractDetailPage.auditArchived'),
        CONSENT_ACCEPTED: 'ESIGN consent accepted',
        SIGN_VIEW_START: 'Started signing'
      }
      return map[type] || (type || this.$t('contractDetailPage.auditUnknown'))
    },
    timelineColor(type) {
      if (type === 'SIGNED') return 'success'
      if (type === 'COMPANY_SIGNED') return 'success'
      if (type === 'ARCHIVED') return 'success'
      if (type === 'SENT_FINAL_EMAIL') return 'success'
      if (type === 'CONSENT_ACCEPTED') return 'primary'
      if (type === 'SENT_INVITE_EMAIL') return 'primary'
      if (type === 'VIEWED' || type === 'SIGN_VIEW_START') return 'info'
      if (type === 'DOWNLOADED') return 'warning'
      return 'info'
    },
    timelineIcon(type) {
      if (type === 'SIGNED' || type === 'COMPANY_SIGNED') return 'el-icon-edit-outline'
      if (type === 'ARCHIVED') return 'el-icon-folder-checked'
      if (type === 'SENT_INVITE_EMAIL' || type === 'SENT_FINAL_EMAIL') return 'el-icon-message'
      if (type === 'CONSENT_ACCEPTED') return 'el-icon-circle-check'
      if (type === 'VIEWED' || type === 'SIGN_VIEW_START') return 'el-icon-view'
      if (type === 'DOWNLOADED') return 'el-icon-download'
      return 'el-icon-info'
    },
    initials(name) {
      if (!name) return '—'
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '—'
      const a = parts[0][0] || ''
      const b = parts.length > 1 ? parts[parts.length - 1][0] : ''
      return (a + b).toUpperCase()
    },
    injectAdminTypeFonts() {
      if (typeof document === 'undefined') return
      if (document.getElementById(ADMIN_TYPE_FONTS_LINK_ID)) return
      const link = document.createElement('link')
      link.id = ADMIN_TYPE_FONTS_LINK_ID
      link.rel = 'stylesheet'
      link.href = ADMIN_TYPE_FONTS_HREF
      document.head.appendChild(link)
    },
    auditEventTagType(type) {
      if (type === 'SIGNED' || type === 'COMPANY_SIGNED' || type === 'ARCHIVED') return 'success'
      if (type === 'DOWNLOADED') return 'warning'
      if (type === 'SENT_FINAL_EMAIL' || type === 'SENT_INVITE_EMAIL') return 'success'
      return 'info'
    },
    async loadPmOptions() {
      this.pmListLoading = true
      try {
        const resp = await getProjectManagerPickerList()
        const rows = (resp && resp.list) || []
        this.pmOptions = rows.map(r => ({
          value: r.id,
          label: `${((r.realName || r.username || '') + '').trim() || this.$t('common.unnamed')}（${r.username || r.id}）`
        }))
      } catch (e) {
        this.pmOptions = []
        this.$message.warning((e && e.message) || this.$t('contractDetailPage.loadPmFailed'))
      } finally {
        this.pmListLoading = false
      }
    },
    async loadContract() {
      this.loading = true
      try {
        const r = await getContractDetail(this.id)
        this.c = r.data !== undefined ? r.data : r
        this.esignRecipientEmail =
          (this.c && this.c.esignRecipientEmail ? String(this.c.esignRecipientEmail) : '') || ''
        await this.loadCustomerSignaturePreview()
        await this.loadPlans()
        if (this.c.linkedProjectId) {
          await this.loadLinkedProjectAttachments()
        } else {
          this.linkedAttachmentsForProject = []
        }
        if (this.c.id) await this.loadChangeOrders()
        await this.loadApprovedChangeOrderDeltaSum()
        this.loadAudits()
      } finally {
        this.loading = false
      }
    },
    async loadLinkedProjectAttachments() {
      this.linkedAttachmentsForProject = []
      if (!this.c || !this.c.linkedProjectId) return
      this.linkedAttachmentsLoading = true
      try {
        const r = await getProjectWorkspace(this.c.linkedProjectId)
        const data = r.data !== undefined ? r.data : r
        this.linkedAttachmentsForProject = data.attachments || []
      } catch (e) {
        this.linkedAttachmentsForProject = []
      } finally {
        this.linkedAttachmentsLoading = false
      }
    },
    async previewProjectAttachment(row) {
      if (!row || typeof row.id !== 'number') return
      const name = (row.fileName != null && String(row.fileName).trim()) || 'download.bin'
      try {
        await previewBlobAuthenticated(previewAttachmentUrl(row.id), name)
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    async downloadProjectAttachment(row) {
      if (!row || typeof row.id !== 'number') return
      const name = (row.fileName != null && String(row.fileName).trim()) || 'download.bin'
      try {
        await downloadBlobAuthenticated(downloadAttachmentUrl(row.id), name)
      } catch (e) {
        this.$message.error(this.$t('common.downloadFailed'))
      }
    },
    async onQuoteExtraFile(e) {
      const file = e.target.files && e.target.files[0]
      if (e.target) e.target.value = ''
      if (!file || !this.c || !this.c.linkedProjectId || !this.c.quoteId) return
      if (!this.canManageQuoteContractExtras) return
      try {
        await uploadProjectAttachment(this.c.linkedProjectId, {
          file,
          quoteId: this.c.quoteId,
          attachmentTypeCode: 4
        })
        this.$message.success(this.$t('contractDetailPage.signedUploaded'))
        await this.loadLinkedProjectAttachments()
      } catch (err) {
        this.$message.error((err && err.message) || this.$t('contractDetailPage.saveFail'))
      }
    },
    async onContractExtraFile(e) {
      const file = e.target.files && e.target.files[0]
      if (e.target) e.target.value = ''
      if (!file || !this.c || !this.c.linkedProjectId || !this.c.id) return
      if (!this.canManageQuoteContractExtras) return
      try {
        await uploadProjectAttachment(this.c.linkedProjectId, {
          file,
          contractId: this.c.id,
          attachmentTypeCode: 4
        })
        this.$message.success(this.$t('contractDetailPage.signedUploaded'))
        await this.loadLinkedProjectAttachments()
      } catch (err) {
        this.$message.error((err && err.message) || this.$t('contractDetailPage.saveFail'))
      }
    },
    async loadApprovedChangeOrderDeltaSum() {
      this.approvedChangeOrderDeltaSum = 0
      if (!this.c || !this.c.id) return
      try {
        const contractId = Number(this.c.id)
        let pageNo = 1
        const pageSize = 100
        const maxPages = 50
        let sum = 0
        while (pageNo <= maxPages) {
          const r = await getChangeOrderPage({ pageNo, pageSize, contractId })
          const d = r.data || r
          const records = d.records || []
          for (const co of records) {
            if (Number(co.approvalStatusCode) === 3 && co.amountDelta != null && co.amountDelta !== '') {
              const n = Number(co.amountDelta)
              if (!Number.isNaN(n)) sum += n
            }
          }
          if (!records.length || records.length < pageSize) break
          pageNo += 1
        }
        this.approvedChangeOrderDeltaSum = sum
      } catch (e) {
        this.approvedChangeOrderDeltaSum = 0
      }
    },
    async loadChangeOrders() {
      if (!this.c || !this.c.id) return
      this.coLoading = true
      try {
        const r = await getChangeOrderPage({
          pageNo: this.coPageNo,
          pageSize: this.coPageSize,
          contractId: Number(this.c.id)
        })
        const d = r.data || r
        this.changeOrders = d.records || []
        this.coTotal = d.total || 0
      } catch (e) {
        this.changeOrders = []
        this.coTotal = 0
      } finally {
        this.coLoading = false
      }
    },
    onCoSizeChange(size) {
      this.coPageSize = size
      this.coPageNo = 1
      this.loadChangeOrders()
    },
    openCoNew() {
      if (!this.c || !this.c.id) return
      this.coForm = { amountDelta: '', descriptionText: '' }
      this.coDlg = true
    },
    resetCoForm() {
      this.coForm = { amountDelta: '', descriptionText: '' }
    },
    async saveCoNew() {
      if (!this.c || !this.c.id) return
      this.coSaving = true
      try {
        const amt = this.coForm.amountDelta === '' || this.coForm.amountDelta == null ? null : Number(this.coForm.amountDelta)
        await createChangeOrder({
          contractId: Number(this.c.id),
          amountDelta: Number.isNaN(amt) ? null : amt,
          descriptionText: this.coForm.descriptionText || null
        })
        this.$message.success(this.$t('changeOrderPage.created'))
        this.coDlg = false
        await this.loadChangeOrders()
        await this.loadApprovedChangeOrderDeltaSum()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.coSaving = false
      }
    },
    onApproveChangeOrder(row) {
      if (!row || row.id == null) return
      this.$confirm(this.$t('changeOrderPage.confirmApprove'), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await approveChangeOrder(row.id)
          this.$message.success(this.$t('common.approved'))
          await this.loadChangeOrders()
          await this.loadApprovedChangeOrderDeltaSum()
        })
        .catch(() => {})
    },
    onRejectChangeOrder(row) {
      if (!row || row.id == null) return
      this.$confirm(this.$t('changeOrderPage.confirmReject'), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          await rejectChangeOrder(row.id)
          this.$message.success(this.$t('common.rejected'))
          await this.loadChangeOrders()
          await this.loadApprovedChangeOrderDeltaSum()
        })
        .catch(() => {})
    },
    cleanupSignaturePreview() {
      if (this.signaturePreviewUrl) {
        window.URL.revokeObjectURL(this.signaturePreviewUrl)
        this.signaturePreviewUrl = ''
      }
    },
    async loadCustomerSignaturePreview() {
      this.cleanupSignaturePreview()
      if (!this.c || !this.c.customerSignatureImageUrl) return
      const token = localStorage.getItem('token')
      try {
        const res = await axios.get(downloadCustomerSignatureUrl(this.id), {
          responseType: 'blob',
          headers: { Authorization: 'Bearer ' + token }
        })
        this.signaturePreviewUrl = window.URL.createObjectURL(new Blob([res.data]))
      } catch (e) {
        this.signaturePreviewUrl = ''
      }
    },
    openEsignTrailDialog() {
      this.esignTrailDlg = true
    },
    async loadAudits() {
      if (!this.id) return
      this.auditLoading = true
      try {
        const r = await listContractSignAudits(this.id)
        const rows = r.data !== undefined ? r.data : r
        this.auditList = Array.isArray(rows) ? rows : []
      } catch (e) {
        this.auditList = []
      } finally {
        this.auditLoading = false
      }
    },
    async loadPlans() {
      if (!this.c || !this.c.id) return
      try {
        if (this.c.linkedProjectId) {
          const r = await listPaymentPlans(this.c.linkedProjectId)
          this.plans = r.data !== undefined ? r.data : r
          this.planSource = 'project'
        } else {
          const r = await listContractPaymentPlans(this.c.id)
          this.plans = r.data !== undefined ? r.data : r
          this.planSource = 'contract'
        }
        if (!Array.isArray(this.plans)) this.plans = []
      } catch (e) {
        this.plans = []
        this.$message.error(e.message || this.$t('common.failed'))
      }
    },
    previewMerged() {
      if (!this.id || !this.hasContractDocument) return
      const fb = this.mergedDownloadFallbackName()
      previewBlobAuthenticated(previewContractDocumentUrl(this.id), fb).catch(() =>
        this.$message.error(this.$t('common.downloadFailed'))
      )
    },
    toggleContractPreview() {
      this.contractPreviewOpen = !this.contractPreviewOpen
      if (this.contractPreviewOpen) {
        this.$nextTick(() => this.maybeLoadContractPdf())
      } else {
        this.resetSignedPdf()
      }
    },
    downloadContractDocument() {
      if (!this.hasContractDocument) return
      const fb = this.mergedDownloadFallbackName()
      downloadBlobAuthenticated(downloadContractDocumentUrl(this.id), fb).catch(() => {
        this.$message.error(this.$t('common.failed'))
      })
    },
    async doRegenerateDocument() {
      if (!this.canRegenerateDocument || this.regenerateLoading) return
      try {
        await this.$confirm(
          this.$t('contractDetailPage.remergeConfirm'),
          this.$t('contractDetailPage.remerge'),
          { type: 'warning' }
        )
      } catch (e) {
        return
      }
      this.regenerateLoading = true
      try {
        const res = await regenerateContractDocument(this.id)
        if (res && res.code != null && res.code !== 200) {
          this.$message.error(res.msg || this.$t('common.failed'))
          return
        }
        this.$message.success(this.$t('contractDetailPage.mergeRegenerated'))
        this.contractPreviewOpen = false
        this.resetSignedPdf()
        await this.loadContract()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.regenerateLoading = false
      }
    },
    mergedDownloadFallbackName() {
      if (!this.c || !this.c.generatedFileUrl) return 'contract-merged.bin'
      const last = String(this.c.generatedFileUrl).split('/').pop() || ''
      const dot = last.lastIndexOf('.')
      const ext = dot >= 0 ? last.substring(dot) : '.bin'
      const no = String(this.c.contractNo || 'contract').replace(/[\\/:*?"<>|]/g, '_')
      return `${no}-merged${ext}`
    },
    async doSendInvite() {
      this.esignSendLoading = true
      try {
        const payload = {}
        if (this.esignRecipientEmail && String(this.esignRecipientEmail).trim()) {
          payload.recipientEmail = String(this.esignRecipientEmail).trim()
        }
        const res = await sendContractToCustomer(this.id, payload)
        if (res && res.code != null && res.code !== 200) {
          this.$message.error(res.msg || this.$t('common.failed'))
          return
        }
        this.$message.success(this.$t('contractDetailPage.esignInviteSent'))
        await this.loadContract()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.esignSendLoading = false
      }
    },
    async doSendFinal() {
      this.esignFinalLoading = true
      try {
        const payload = {}
        if (this.esignRecipientEmail && String(this.esignRecipientEmail).trim()) {
          payload.recipientEmail = String(this.esignRecipientEmail).trim()
        }
        const res = await sendFinalContractToCustomer(this.id, payload)
        if (res && res.code != null && res.code !== 200) {
          this.$message.error(res.msg || this.$t('common.failed'))
          return
        }
        this.$message.success(this.$t('contractDetailPage.esignFinalSent'))
        await this.loadContract()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.esignFinalLoading = false
      }
    },
    async maybeLoadContractPdf() {
      if (!this.contractPreviewOpen || !this.hasContractDocument) {
        this.resetSignedPdf()
        return
      }
      if (this.signedPdfLoading) return
      if (this.signedPdfLoadedKey && this.signedPdfLoadedKey === this.contractDocumentKey) return
      this.signedPdfLoading = true
      this.signedPdfError = ''
      try {
        const token = localStorage.getItem('token')
        const res = await axios.get(previewContractDocumentUrl(this.id) + '?_=' + Date.now(), {
          responseType: 'arraybuffer',
          headers: token ? { Authorization: 'Bearer ' + token } : {}
        })
        const pdfjsLib = await import(/* webpackChunkName: "pdfjs" */ 'pdfjs-dist/legacy/build/pdf')
        const pdfWorker =
          (process && process.env && process.env.VUE_APP_PDFJS_WORKER_URL) ||
          'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.16.105/legacy/build/pdf.worker.min.js'
        pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorker
        const loadingTask = pdfjsLib.getDocument({ data: new Uint8Array(res.data) })
        this.signedPdfDoc = await loadingTask.promise
        const total = this.signedPdfDoc.numPages
        this.signedPdfPages = []
        for (let i = 1; i <= total; i++) {
          this.signedPdfPages.push({ num: i })
        }
        await this.$nextTick()
        for (let i = 1; i <= total; i++) {
          await this.renderSignedPdfPage(i)
        }
        this.signedPdfLoadedKey = this.contractDocumentKey
      } catch (e) {
        this.signedPdfPages = []
        this.signedPdfError = (e && e.message) || ''
      } finally {
        this.signedPdfLoading = false
      }
    },
    async renderSignedPdfPage(pageNum) {
      if (!this.signedPdfDoc) return
      const page = await this.signedPdfDoc.getPage(pageNum)
      const canvasRef = this.$refs['signedPdfCanvas' + (pageNum - 1)]
      const canvas = Array.isArray(canvasRef) ? canvasRef[0] : canvasRef
      if (!canvas) return
      const dpr = window.devicePixelRatio || 1
      const viewport = page.getViewport({ scale: 1.2 })
      canvas.width = Math.floor(viewport.width * dpr)
      canvas.height = Math.floor(viewport.height * dpr)
      canvas.style.width = viewport.width + 'px'
      canvas.style.height = viewport.height + 'px'
      const ctx = canvas.getContext('2d')
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
      await page.render({ canvasContext: ctx, viewport }).promise
    },
    resetSignedPdf() {
      this.signedPdfDoc = null
      this.signedPdfPages = []
      this.signedPdfError = ''
      this.signedPdfLoadedKey = ''
    },
    openCompanySign() {
      const u = store.state.userInfo || {}
      this.companySignForm.signerName = (u.realName || u.username || '').trim()
      this.companyTypeName = this.companySignForm.signerName
      this.companyMethod = 'draw'
      this.companyTypeFontIndex = 0
      this.injectAdminTypeFonts()
      this.companySignDlg = true
    },
    setCompanyMethod(m) {
      this.companyMethod = m
      if (m === 'draw') {
        this.$nextTick(() => this.prepareCompanyCanvas())
      }
    },
    onCompanySignOpened() {
      this.$nextTick(() => this.prepareCompanyCanvas())
    },
    renderCompanyTypedPng() {
      const font = ADMIN_TYPE_FONTS[this.companyTypeFontIndex] || ADMIN_TYPE_FONTS[0]
      const canvas = document.createElement('canvas')
      const w = 760
      const h = 220
      canvas.width = w
      canvas.height = h
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, w, h)
      ctx.fillStyle = '#0f172a'
      ctx.textBaseline = 'middle'
      ctx.textAlign = 'center'
      ctx.font = '64px ' + font.css
      ctx.fillText(this.companyTypeName || this.companySignForm.signerName || 'Signer', w / 2, h / 2)
      return canvas.toDataURL('image/png')
    },
    prepareCompanyCanvas() {
      const canvas = this.$refs.companySignCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      ctx.fillStyle = '#f8fafc'
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      ctx.strokeStyle = '#1f2937'
      ctx.lineWidth = 2
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      ctx.font = '14px sans-serif'
      ctx.fillStyle = '#64748b'
      ctx.fillText(this.$t('publicContract.signHere'), 12, 24)
      this.companySignHasStroke = false
    },
    pointCompanySign(e) {
      const canvas = this.$refs.companySignCanvas
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()
      const src = e.touches && e.touches[0] ? e.touches[0] : e
      return {
        x: (src.clientX - rect.left) * (canvas.width / rect.width),
        y: (src.clientY - rect.top) * (canvas.height / rect.height)
      }
    },
    startCompanySign(e) {
      const canvas = this.$refs.companySignCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const p = this.pointCompanySign(e)
      ctx.strokeStyle = '#111827'
      ctx.beginPath()
      ctx.moveTo(p.x, p.y)
      this.companySignDrawing = true
      this.companySignHasStroke = true
    },
    moveCompanySign(e) {
      if (!this.companySignDrawing) return
      const canvas = this.$refs.companySignCanvas
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const p = this.pointCompanySign(e)
      ctx.lineTo(p.x, p.y)
      ctx.stroke()
    },
    endCompanySign() {
      this.companySignDrawing = false
    },
    clearCompanySign() {
      this.prepareCompanyCanvas()
    },
    resetCompanySign() {
      this.companySignDrawing = false
      this.companySignHasStroke = false
    },
    async submitCompanySign() {
      if (!this.companySignForm.signerName || !String(this.companySignForm.signerName).trim()) {
        this.$message.warning(this.$t('contractDetailPage.companySignerNameRequired'))
        return
      }
      let dataUrl = ''
      let methodCode = 1
      if (this.companyMethod === 'draw') {
        if (!this.companySignHasStroke) {
          this.$message.warning(this.$t('publicContract.signRequired'))
          return
        }
        const canvas = this.$refs.companySignCanvas
        dataUrl = canvas ? canvas.toDataURL('image/png') : ''
        methodCode = 1
      } else if (this.companyMethod === 'type') {
        if (!this.companyTypeName || !this.companyTypeName.trim()) {
          this.$message.warning('Please type the name to use as signature.')
          return
        }
        dataUrl = this.renderCompanyTypedPng()
        methodCode = 2
      }
      this.companySigning = true
      try {
        const res = await companySignContract(this.id, {
          signerName: String(this.companySignForm.signerName).trim(),
          signatureDataUrl: dataUrl,
          signatureMethod: methodCode
        })
        if (res && res.code != null && res.code !== 200) {
          this.$message.error(res.msg || this.$t('common.failed'))
          return
        }
        this.$message.success(this.$t('common.saved'))
        this.companySignDlg = false
        await this.loadContract()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('common.failed'))
      } finally {
        this.companySigning = false
      }
    },
    downloadCustomerSignature() {
      if (!this.c || !this.c.customerSignatureImageUrl) {
        this.$message.warning(this.$t('contractDetailPage.noSignatureImg'))
        return
      }
      const token = localStorage.getItem('token')
      axios.get(downloadCustomerSignatureUrl(this.id), {
        responseType: 'blob',
        headers: { Authorization: 'Bearer ' + token }
      }).then(res => {
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = 'customer-signature.png'
        a.click()
        window.URL.revokeObjectURL(url)
      }).catch(() => this.$message.error(this.$t('common.downloadFailed')))
    },
    onProjectHeaderAction() {
      if (!this.c) return
      if (this.c.linkedProjectId) {
        this.goLinkedProject()
      } else {
        this.pjName = this.contractCustomerDisplayNameForProject()
        this.projectDlg = true
      }
    },
    goLinkedProject() {
      if (!this.c || !this.c.linkedProjectId) return
      this.$router.push(projectDetailLocation(this.c.linkedProjectId, this.$route.fullPath))
    },
    /** 报价快照客户姓名（名+姓）；若无则退回签署人姓名，供默认项目名称 */
    contractCustomerDisplayNameForProject() {
      if (!this.c) return ''
      const snap = this.c.linkedQuoteCustomerDisplayName
      if (snap != null && String(snap).trim()) return String(snap).trim()
      const signer = this.c.customerSignedName
      if (signer != null && String(signer).trim()) return String(signer).trim()
      return ''
    },
    async createProject() {
      if (this.pmUserId == null || this.pmUserId === '') {
        this.$message.warning(this.$t('contractDetailPage.selectPmWarn'))
        return
      }
      this.projectCreating = true
      try {
        await createProjectFromContract(this.id, {
          projectManagerUserId: Number(this.pmUserId),
          projectName:
            (this.pjName || '').trim() ||
            this.contractCustomerDisplayNameForProject() ||
            this.$t('contractDetailPage.defaultProjectName')
        })
        this.$message.success(this.$t('contractDetailPage.projectCreated'))
        this.projectDlg = false
        await this.loadContract()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('contractDetailPage.actionFail'))
      } finally {
        this.projectCreating = false
      }
    },
    openPlanNew() {
      if (!this.canEditPaymentPlans) return
      this.planEditRow = null
      const next = (this.plans.length || 0) + 1
      this.planForm = {
        nodeName: this.$t('contractDetailPage.defaultNodePrefix') + next,
        plannedAmount: '',
        plannedDate: '',
        remarkText: ''
      }
      this.planDlg = true
    },
    onPlanDlgClosed() {
      this.planEditRow = null
      this.planForm = null
    },
    isPaymentPlanEditable(row) {
      if (this.planSource === 'contract') return row != null
      return row != null && Number(row.paymentStatusCode) === 1
    },
    planActualAmountFromRow(row) {
      if (!row || row.actualAmount == null || row.actualAmount === '') return null
      const n = Number(row.actualAmount)
      return Number.isNaN(n) ? null : n
    },
    removePlan(row) {
      if (!this.canEditPaymentPlans) return
      if (!row || row.id == null) return
      if (!this.isPaymentPlanEditable(row)) {
        this.$message.warning(this.$t('contractDetailPage.planMutateOnlyPlanning'))
        return
      }
      this.$confirm(this.$t('contractDetailPage.deletePlanConfirm'), this.$t('common.tip'), { type: 'warning' })
        .then(async () => {
          if (this.planSource === 'contract') {
            await deleteContractPaymentPlan(this.c.id, row.id)
          } else {
            await deletePaymentPlan(row.id)
          }
          this.$message.success(this.$t('contractDetailPage.deleted'))
          await this.loadPlans()
        })
        .catch(() => {})
    },
    openPlanEdit(row) {
      if (!this.canEditPaymentPlans) return
      if (!row) return
      if (!this.isPaymentPlanEditable(row)) {
        this.$message.warning(this.$t('contractDetailPage.planMutateOnlyPlanning'))
        return
      }
      this.planEditRow = row
      this.planForm = {
        nodeName: row.nodeName != null ? String(row.nodeName) : '',
        plannedAmount: row.plannedAmount != null ? String(row.plannedAmount) : '',
        plannedDate: row.plannedDate || '',
        remarkText: row.remarkText != null ? String(row.remarkText) : ''
      }
      this.planDlg = true
    },
    numOrNull(s) {
      if (s === '' || s == null) return null
      const n = Number(s)
      return Number.isNaN(n) ? null : n
    },
    validatePlanForm() {
      const f = this.planForm
      if (!f) return false
      const name = (f.nodeName != null ? String(f.nodeName) : '').trim()
      if (!name) {
        this.$message.warning(this.$t('contractDetailPage.planValidateNodeName'))
        return false
      }
      const amtRaw = f.plannedAmount
      if (amtRaw === '' || amtRaw == null || String(amtRaw).trim() === '') {
        this.$message.warning(this.$t('contractDetailPage.planValidatePlannedAmount'))
        return false
      }
      const amt = Number(String(amtRaw).trim())
      if (Number.isNaN(amt)) {
        this.$message.warning(this.$t('contractDetailPage.planValidatePlannedAmount'))
        return false
      }
      const date = f.plannedDate != null ? String(f.plannedDate).trim() : ''
      if (!date) {
        this.$message.warning(this.$t('contractDetailPage.planValidatePlannedDate'))
        return false
      }
      return true
    },
    async savePlanEdit() {
      if (!this.canEditPaymentPlans) return
      if (!this.planForm) return
      if (!this.validatePlanForm()) return
      const row = this.planEditRow
      this.planSaving = true
      try {
        if (this.planSource === 'contract') {
          const body = {
            nodeName: this.planForm.nodeName || null,
            plannedAmount: this.numOrNull(this.planForm.plannedAmount),
            plannedDate: this.planForm.plannedDate || null,
            remarkText: this.planForm.remarkText || null
          }
          if (row) {
            body.nodeSeq = row.nodeSeq
            await updateContractPaymentPlan(this.c.id, row.id, body)
          } else {
            body.nodeSeq = (this.plans.length || 0) + 1
            await saveContractPaymentPlan(this.c.id, body)
          }
        } else if (row) {
          if (!this.isPaymentPlanEditable(row)) {
            this.$message.warning(this.$t('contractDetailPage.planMutateOnlyPlanning'))
            return
          }
          const payload = {
            id: row.id,
            projectId: row.projectId,
            nodeSeq: row.nodeSeq,
            nodeName: this.planForm.nodeName || null,
            plannedAmount: this.numOrNull(this.planForm.plannedAmount),
            plannedDate: this.planForm.plannedDate || null,
            actualAmount: this.planActualAmountFromRow(row),
            actualDate: row.actualDate || null,
            paymentStatusCode: 1,
            remarkText: this.planForm.remarkText || null,
            invoiceId: row.invoiceId != null ? row.invoiceId : null,
            changeOrderId: row.changeOrderId != null ? row.changeOrderId : null
          }
          await updatePaymentPlan(row.id, payload)
        } else {
          if (!this.c.linkedProjectId) return
          await savePaymentPlan({
            projectId: this.c.linkedProjectId,
            nodeSeq: (this.plans.length || 0) + 1,
            nodeName: this.planForm.nodeName || null,
            plannedAmount: this.numOrNull(this.planForm.plannedAmount),
            plannedDate: this.planForm.plannedDate || null,
            paymentStatusCode: 1,
            remarkText: this.planForm.remarkText || null
          })
        }
        this.$message.success(this.$t('contractDetailPage.planSaved'))
        this.planDlg = false
        await this.loadPlans()
      } catch (e) {
        this.$message.error((e && e.message) || this.$t('contractDetailPage.saveFail'))
      } finally {
        this.planSaving = false
      }
    }
  }
}
</script>

<style scoped>
.main-card {
  border-radius: 8px;
  width: 100%;
  max-width: none;
  box-sizing: border-box;
}

.detail-tabs {
  margin-top: 4px;
}

.contract-detail__tab-pane-body {
  min-width: 0;
  padding-top: 4px;
}

.section-hdr--split {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
}

.audit-filter-hint {
  margin: 0 0 10px;
  font-size: 12px;
  line-height: 1.45;
  color: rgba(255, 255, 255, 0.55);
}

.audit-filter-hint.muted {
  color: rgba(255, 255, 255, 0.45);
}

.section-hdr__action {
  flex-shrink: 0;
  padding: 0 !important;
  font-weight: 500;
}

.contract-detail__summary {
  margin-bottom: 18px;
}

.contract-detail__docs-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  margin-bottom: 14px;
}

@media (min-width: 960px) {
  .contract-detail__docs-grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.contract-detail__docs-grid .section--doc {
  margin-bottom: 0;
  min-width: 0;
}

.desc-block--flush {
  margin-bottom: 0;
}

.co-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 16px;
  margin-bottom: 12px;
}

.co-toolbar__link {
  color: var(--dark-text-sub, #8b95a1) !important;
}

.co-toolbar__link:hover {
  color: var(--dark-accent, #4ade80) !important;
}

.co-table {
  width: 100%;
}

.list-pager {
  margin-top: 16px;
  text-align: right;
}

.contract-detail__co-section {
  margin-bottom: 0;
}

.contract-detail__co-in-base {
  margin-top: 14px;
}

.contract-detail__plan-card >>> .el-card__body {
  overflow-x: auto;
}

.co-toolbar--inline {
  margin-bottom: 10px;
}

.desc-block {
  margin-bottom: 16px;
}

.section {
  margin-bottom: 14px;
  border-radius: 12px;
  background: var(--color-surface) !important;
  border: 1px solid var(--color-border) !important;
  box-shadow: var(--shadow-card);
}

.section >>> .el-card__header {
  padding: 12px 16px;
  background: linear-gradient(180deg, var(--color-surface) 0%, var(--color-bg-soft) 100%) !important;
  border-bottom: 1px solid var(--color-border-soft) !important;
}

.section >>> .el-card__body {
  padding: 14px 16px;
}

.section-hdr {
  font-weight: 600;
  font-size: 13px;
  color: var(--color-text);
}

.section-tip {
  margin: 0 0 12px;
  font-size: 12px;
  color: var(--color-text-sub);
  line-height: 1.5;
}

.section-tip.ok {
  color: #67c23a;
  margin-bottom: 0;
}

.signed-uploaded-row {
  margin: -4px 0 10px;
  font-size: 12px;
  line-height: 1.5;
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 6px 8px;
}

.signed-uploaded-name {
  font-weight: 600;
  color: var(--color-text);
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.btn-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

.upload-row {
  align-items: center;
}

.extra-att-hdr {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text);
  margin: 14px 0 8px;
}

.extra-att-list {
  list-style: none;
  margin: 0 0 12px;
  padding: 0;
}

.extra-att-list li {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 6px 0;
  border-bottom: 1px solid var(--color-border-soft);
  font-size: 13px;
}

.extra-att-list__name {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hidden-file {
  display: none;
}

.project-dlg-tip {
  margin-top: 0;
}

.project-dlg-form {
  margin-top: 4px;
}

.project-dlg-pm {
  width: 100%;
}

.plan-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

@media (max-width: 720px) {
  .plan-stats {
    grid-template-columns: 1fr;
  }
}

.plan-stat-card {
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--color-surface-soft);
  border: 1px solid var(--color-border-soft);
  min-width: 0;
}

.plan-stat-card__label {
  display: block;
  font-size: 12px;
  color: var(--color-text-sub);
  margin-bottom: 6px;
  line-height: 1.35;
}

.plan-stat-card__value {
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text);
  line-height: 1.25;
}

.plan-toolbar {
  margin-bottom: 10px;
}

.plan-table {
  width: 100%;
}

.plan-table ::v-deep th .cell {
  white-space: nowrap;
}

.text-muted {
  color: var(--color-text-muted);
}

.link {
  color: #409eff;
  text-decoration: none;
}

.link:hover {
  text-decoration: underline;
}

.danger-link {
  color: #f56c6c;
}

.plan-hint {
  font-size: 12px;
  color: var(--color-text-sub);
  margin: 0 0 10px;
  line-height: 1.5;
}

.esign-grid {
  display: grid;
  grid-template-columns: minmax(240px, 360px) 1fr;
  gap: 16px;
  align-items: start;
}

.esign-preview {
  padding: 12px;
  border: 1px solid var(--color-border-soft);
  border-radius: 10px;
  background: var(--color-surface-soft);
}

.esign-image {
  display: block;
  width: 100%;
  border-radius: 6px;
  background: var(--color-surface);
}

.esign-meta {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.esign-audit-table {
  margin-top: 16px;
}

.esign-row {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  font-size: 13px;
  color: var(--color-text-sub);
}

.esign-row span {
  width: 72px;
  color: var(--color-text-muted);
  flex: 0 0 auto;
}

.esign-row strong {
  color: var(--color-text);
  font-weight: 500;
}

.ua-text {
  word-break: break-all;
}

.esign-trail-dlg-toolbar {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 10px;
}

.contract-company-pad {
  border: 1px solid var(--color-border-soft);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 8px;
}

.contract-company-pad .signature-pad__canvas {
  display: block;
  width: 100%;
  height: auto;
}

/* ===== DocuSign-style envelope header ===== */
.ds-envelope {
  margin-bottom: 14px;
  border-radius: 14px;
  background: linear-gradient(135deg, #1e293b 0%, #0f172a 100%);
  color: #f8fafc;
  padding: 22px 26px;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.18);
}

.ds-envelope__row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 18px;
  flex-wrap: wrap;
}

.ds-envelope__eyebrow {
  color: #93c5fd;
  font-size: 11px;
  letter-spacing: 0.18em;
  font-weight: 700;
}

.ds-envelope__no {
  font-size: 22px;
  font-weight: 700;
  margin-top: 4px;
}

.ds-envelope__title {
  color: #cbd5e1;
  margin-top: 4px;
  font-size: 13px;
}

.ds-envelope__legacy-tip {
  margin: 8px 0 0;
  font-size: 12px;
  color: rgba(203, 213, 225, 0.88);
  line-height: 1.45;
  max-width: 520px;
}

.ds-envelope__badge {
  font-size: 12px;
  letter-spacing: 0.08em;
  font-weight: 700;
  padding: 8px 14px;
  border-radius: 999px;
  text-transform: uppercase;
  white-space: nowrap;
}

.ds-envelope__badge--draft { background: rgba(148, 163, 184, 0.25); color: #e2e8f0; }
.ds-envelope__badge--pending { background: rgba(251, 191, 36, 0.18); color: #fde68a; }
.ds-envelope__badge--progress { background: rgba(96, 165, 250, 0.18); color: #bfdbfe; }
.ds-envelope__badge--completed { background: rgba(74, 222, 128, 0.22); color: #bbf7d0; }
.ds-envelope__badge--cancelled { background: rgba(248, 113, 113, 0.2); color: #fecaca; }

/* signed contract preview card (CRM admin side) */
.signed-preview-card {
  margin-top: 18px;
}

.section-hdr__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  justify-content: flex-end;
}

.contract-preview-collapsed-hint {
  margin: 0;
  padding: 8px 4px 4px;
  font-size: 13px;
}

.signed-pdf-wrap {
  background: #e2e8f0;
  border-radius: 10px;
  max-height: 60vh;
  min-height: 280px;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}

.signed-pdf-canvas {
  background: #fff;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.12);
  border-radius: 4px;
  max-width: 100%;
}

.signed-pdf-loading,
.signed-pdf-error {
  color: #475569;
  background: #fff;
  border-radius: 8px;
  padding: 24px;
  text-align: center;
}

.ds-envelope__cta {
  margin-top: 16px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ===== Recipients ===== */
.ds-recipients {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 14px;
}

@media (max-width: 760px) {
  .ds-recipients {
    grid-template-columns: 1fr;
  }
}

.ds-recipient {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 12px;
  background: var(--color-surface);
  border: 1px solid var(--color-border-soft);
  transition: border-color 160ms ease;
}

.ds-recipient--signed { border-color: rgba(74, 222, 128, 0.6); background: rgba(74, 222, 128, 0.06); }
.ds-recipient--sent { border-color: rgba(251, 191, 36, 0.55); }

.ds-recipient__avatar {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6366f1 0%, #2563eb 100%);
  color: #fff;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  letter-spacing: 0.04em;
}

.ds-recipient__avatar--co { background: linear-gradient(135deg, #f59e0b 0%, #ea580c 100%); }

.ds-recipient__body {
  flex: 1;
  min-width: 0;
}

.ds-recipient__role {
  font-size: 10px;
  letter-spacing: 0.16em;
  color: var(--color-text-muted);
  font-weight: 700;
}

.ds-recipient__name {
  font-weight: 700;
  color: var(--color-text);
  font-size: 14px;
  margin-top: 2px;
}

.ds-recipient__email {
  font-size: 12px;
  color: var(--color-text-sub);
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ds-recipient__state {
  margin-top: 6px;
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.ds-recipient__invite {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--color-border-soft);
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}

.ds-recipient__invite-input {
  flex: 1;
  min-width: 180px;
  max-width: 320px;
}

.ds-recipient__company-act {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px dashed var(--color-border-soft);
}

.ds-recipient__when {
  font-size: 11px;
  color: var(--color-text-muted);
}

.ds-recipient__thumb img {
  width: 60px;
  height: 36px;
  object-fit: contain;
  background: #fff;
  border-radius: 4px;
  padding: 2px;
}

/* ===== Activity timeline ===== */
.ds-timeline-card {
  margin-bottom: 14px;
}

.ds-timeline-loading,
.ds-timeline-empty {
  padding: 12px 8px;
  color: var(--color-text-sub);
  font-size: 13px;
}

.ds-timeline-row {
  font-size: 13px;
  color: var(--color-text);
}

.ds-timeline-row .text-muted {
  color: var(--color-text-muted);
  margin-left: 2px;
}

.ds-timeline-note {
  margin-top: 4px;
  color: var(--color-text-sub);
  font-size: 12px;
  line-height: 1.5;
}

/* ===== Company sign dialog: method tabs ===== */
.ds-company-sign__lead {
  margin: 0 0 10px;
  font-size: 13px;
  color: var(--color-text-sub);
}

.ds-company-sign__row {
  margin-bottom: 12px;
}

.ds-method-tabs {
  display: flex;
  gap: 0;
  border: 1px solid var(--color-border-soft);
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 10px;
  width: fit-content;
}

.ds-method-tabs--admin {
  margin-top: -4px;
}

.ds-method-tab {
  background: var(--color-surface);
  border: none;
  color: var(--color-text-sub);
  padding: 8px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
}

.ds-method-tab:not(:last-child) {
  border-right: 1px solid var(--color-border-soft);
}

.ds-method-tab--active {
  background: #2563eb;
  color: #fff;
}

.ds-company-pane-actions {
  margin-top: 4px;
}

.ds-type-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.ds-type-option {
  background: var(--color-surface);
  border: 1px solid var(--color-border-soft);
  border-radius: 8px;
  padding: 12px 14px;
  cursor: pointer;
  min-height: 76px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: all 150ms ease;
}

.ds-type-option:hover {
  border-color: #2563eb;
}

.ds-type-option--active {
  border-color: #2563eb;
  background: rgba(37, 99, 235, 0.06);
  box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.15);
}

.ds-type-sample {
  font-size: 28px;
  line-height: 1.1;
  color: var(--color-text);
}

.ds-type-style {
  font-family: inherit;
  font-size: 11px;
  color: var(--color-text-muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

<style>
.contract-esign-trail-dlg {
  max-width: 960px;
}

.contract-esign-trail-dlg .el-dialog__body {
  padding: 8px 20px 18px;
  max-height: calc(100vh - 110px);
  overflow-y: auto;
  box-sizing: border-box;
}
</style>
